<template>
  <!-- prettier-ignore -->
  <div
    :id="`upsell_${$props.product.productId}_${$props.person.number}`"
    class="upsell"
  >
    <basic-link
      occurence="inline"
      :label="productLabel"
      @click="openPreview"
    />
    <span
      class="body-2"
      v-html="upsellLabel"
    />
    <basic-text-button
      :label="isUpgradeProduct ? $t('product.actions.add') : $t('product.actions.replace')"
      @click="add"
    >
      <template #trailing>
        <the-icon
          art="solid"
          name="add"
        />
      </template>
    </basic-text-button>
  </div>
</template>

<script setup>
  import { computed } from 'vue'

  import { events$ } from '@shared/services'

  import useI18n from '@shared/hooks/useI18n'
  import useProduct from '@shared/hooks/useProduct'

  import { BasicLink, BasicTextButton } from '@shared/components/Basic'

  import { EVENT_PRODUCT, SOURCE } from '@shared/config/events'

  // HOOKS
  const { t } = useI18n()
  const { isUpgrade } = useProduct()

  // INIT
  const emit = defineEmits(['add', 'open'])
  const props = defineProps({
    categoryId: {
      type: String,
      required: true,
    },
    person: {
      type: Object,
      required: true,
    },
    personId: {
      type: String,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
    upsellProduct: {
      type: Object,
      required: true,
    },
  })

  // COMPUTED
  const isUpgradeProduct = computed(() => {
    return isUpgrade(props.upsellProduct.productId)
  })

  const price = computed(() => {
    const upsellPrice = parseFloat(props.upsellProduct.prices.find(p => p.selected).price.toFixed(2))

    if (isUpgradeProduct.value) {
      return upsellPrice
    } else {
      const original = props.product.price.price
      return parseFloat(upsellPrice - original).toFixed(2)
    }
  })

  const productLabel = computed(() => {
    return t(`products.${props.upsellProduct.productId}.name`)
  })

  const upsellLabel = computed(() => {
    let __price = price.value
    const positive = __price >= 0
    const labelKey = positive ? 'positive' : 'negative'

    if (!positive) {
      __price = Math.abs(__price).toFixed(2)
    } else {
      __price = parseFloat(__price).toFixed(2)
    }

    return t(`product.upselling.${labelKey}`, { price: __price })
  })

  // METHODS
  function add() {
    emit('add', {
      productId: props.upsellProduct.productId,
      personId: props.personId,
      categoryId: props.categoryId,
      mode: isUpgradeProduct.value ? 'ADD' : 'REPLACE',
      source: SOURCE.UPSELLING,
    })
  }

  function openPreview() {
    emit('open')
    events$.emit(EVENT_PRODUCT.DETAIL_OPEN, {
      categoryId: props.categoryId,
      productId: props.upsellProduct.productId,
      source: SOURCE.UPSELLING,
    })
  }
</script>

<style scoped>
.upsell {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: var(--fixed-spacing-fix-06);
}
</style>
